import { useCallback, useEffect, useRef, useState } from 'react';
import { CLAIMS_LEVEL, ClaimsLogic } from '@/modules/idv';
import { postIdvConfirmVerification } from '@/modules/idv/idv';
import { useUserContext } from '@/modules/user/useUserContext';
import { SixDigitCode } from '@/models/six_digit_code';
import { apiCall, isNetworkError, NETWORK_ERROR_MESSAGE } from '@/util/api';
import type { Phone } from '@/modules/phone';

export type PhoneVerificationStatus =
  | 'idle'
  | 'sending'
  | 'awaiting-code'
  | 'verifying'
  | 'verified'
  | 'expired';

export interface UsePhoneVerificationArgs {
  phone: Phone | null | undefined;
  continuePath: string;
  // For a phone-number change, the number being moved away from. Passed to the
  // confirm call so the backend can text the old number a change notice; omit it
  // when the number is unchanged or being set for the first time.
  previousPhone?: string;
  // For a phone-number migration (new SIM), the client-computed OPRF of the new
  // number and its last two digits. Sent with the initiation request so the
  // backend can move the user's profile onto the new number once it's verified.
  // Omit when the number is unchanged. The OPRF is computed via the oblivious
  // protocol (the backend never sees the plaintext used to derive it).
  migrate?: { phoneOprf: string; phoneTail: string };
  onSuccess?: () => void;
  onExpired?: () => void;
  onInvalidCode?: (message: string) => void;
}

export interface UsePhoneVerification {
  status: PhoneVerificationStatus;
  code: string;
  setCode: (code: string) => void;
  errorMessage: string | null;
  sendSms: () => Promise<void>;
  confirmCode: () => Promise<void>;
  resend: () => Promise<void>;
  reset: () => void;
}

const INVALID_CODE_DEFAULT_MESSAGE = 'Invalid verification code';

// The backend returns error bodies either as plain text or as a JSON object
// like {"error": "phone_mismatch", "message": "..."}. Surface the human-readable
// `message` when present so users don't see raw JSON in a toast.
function extractErrorMessage(body: string): string {
  const trimmed = body.trim();
  if (!trimmed.startsWith('{')) {
    return trimmed;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.message === 'string' && parsed.message) {
      return parsed.message;
    }
  } catch {
    // Not JSON after all — fall back to the raw text.
  }
  return trimmed;
}

export function usePhoneVerification({
  phone,
  continuePath,
  previousPhone,
  migrate,
  onSuccess,
  onExpired,
  onInvalidCode,
}: UsePhoneVerificationArgs): UsePhoneVerification {
  const { claims, refreshUserContext } = useUserContext();

  const [status, setStatus] = useState<PhoneVerificationStatus>('idle');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationExpiryMs, setVerificationExpiryMs] = useState<
    number | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSuccessRef = useRef(onSuccess);
  const onExpiredRef = useRef(onExpired);
  const onInvalidCodeRef = useRef(onInvalidCode);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onExpiredRef.current = onExpired;
    onInvalidCodeRef.current = onInvalidCode;
  }, [onSuccess, onExpired, onInvalidCode]);

  const prevPhoneVerifiedRef = useRef(ClaimsLogic.isPhoneVerified(claims));
  const isConfirmingRef = useRef(false);

  const reset = useCallback(() => {
    setStatus('idle');
    setCode('');
    setVerificationId(null);
    setVerificationExpiryMs(null);
    setErrorMessage(null);
    prevPhoneVerifiedRef.current = ClaimsLogic.isPhoneVerified(claims);
  }, [claims]);

  const sendSms = useCallback(async () => {
    if (!phone) {
      throw new Error('Phone number is required for phone verification');
    }
    setStatus('sending');
    setErrorMessage(null);
    try {
      const response = await apiCall('/api/idv/verifications', {
        method: 'POST',
        body: JSON.stringify({
          level: CLAIMS_LEVEL.PHONE,
          phone: phone.value,
          continue: continuePath,
          // Migration only: the new number's client-computed OPRF + tail so the
          // backend can move the profile onto it once verified.
          ...(migrate
            ? { phoneOprf: migrate.phoneOprf, phoneTail: migrate.phoneTail }
            : {}),
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          extractErrorMessage(errorText) ||
            'Failed to initiate identity verification'
        );
      }
      const data = await response.json();
      setVerificationId(data.id);
      setVerificationExpiryMs(
        typeof data.expiresInMs === 'number' ? data.expiresInMs : null
      );
      prevPhoneVerifiedRef.current = ClaimsLogic.isPhoneVerified(claims);
      setStatus('awaiting-code');
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to send SMS'
      );
      setStatus('idle');
      throw error;
    }
  }, [phone, continuePath, migrate, claims]);

  const confirmCode = useCallback(async () => {
    if (!verificationId) {
      const message = 'Verification ID is missing. Please try again.';
      setErrorMessage(message);
      onInvalidCodeRef.current?.(message);
      return;
    }
    isConfirmingRef.current = true;
    setStatus('verifying');
    setErrorMessage(null);
    try {
      const sixDigitCode = SixDigitCode.create(code);
      await postIdvConfirmVerification(
        verificationId,
        undefined,
        sixDigitCode,
        previousPhone
      );
      await refreshUserContext();
      setStatus('verified');
      onSuccessRef.current?.();
    } catch (error) {
      // A dropped confirm request (no HTTP response — iOS reports "Load failed")
      // is not a wrong code. Report it as a network problem so the user retries
      // rather than thinking their valid code was rejected (#386).
      const message = isNetworkError(error)
        ? NETWORK_ERROR_MESSAGE
        : error instanceof Error && error.message
          ? error.message
          : INVALID_CODE_DEFAULT_MESSAGE;
      setErrorMessage(message);
      setCode('');
      setStatus('awaiting-code');
      onInvalidCodeRef.current?.(message);
    } finally {
      isConfirmingRef.current = false;
    }
  }, [code, refreshUserContext, verificationId, previousPhone]);

  const resend = useCallback(async () => {
    setCode('');
    setVerificationId(null);
    setVerificationExpiryMs(null);
    setErrorMessage(null);
    await sendSms();
  }, [sendSms]);

  // Auto-confirm once 6 digits are entered.
  useEffect(() => {
    if (
      status === 'awaiting-code' &&
      code.length === 6 &&
      verificationId !== null
    ) {
      void confirmCode();
    }
  }, [code, status, verificationId, confirmCode]);

  // Detect verification completing via the SMS magic link (claims update
  // outside of confirmCode).
  useEffect(() => {
    if (status !== 'awaiting-code' && status !== 'verifying') {
      prevPhoneVerifiedRef.current = ClaimsLogic.isPhoneVerified(claims);
      return;
    }
    const currentPhoneVerified = ClaimsLogic.isPhoneVerified(claims);
    if (!prevPhoneVerifiedRef.current && currentPhoneVerified) {
      setStatus('verified');
      onSuccessRef.current?.();
    }
    prevPhoneVerifiedRef.current = currentPhoneVerified;
  }, [claims, status]);

  // Periodically refresh user context while waiting so claims stay fresh.
  useEffect(() => {
    if (status !== 'awaiting-code') return;
    const intervalId = setInterval(() => {
      if (!isConfirmingRef.current) {
        refreshUserContext().catch(error => {
          // eslint-disable-next-line no-console
          console.error('Failed to refresh user context:', error);
        });
      }
    }, 2000);
    return () => clearInterval(intervalId);
  }, [status, refreshUserContext]);

  // Backend-reported expiry timeout.
  useEffect(() => {
    if (status !== 'awaiting-code' || verificationExpiryMs == null) return;
    const timeoutId = setTimeout(() => {
      setStatus('expired');
      onExpiredRef.current?.();
    }, verificationExpiryMs);
    return () => clearTimeout(timeoutId);
  }, [status, verificationExpiryMs]);

  return {
    status,
    code,
    setCode,
    errorMessage,
    sendSms,
    confirmCode,
    resend,
    reset,
  };
}
